import { useEffect, useState, useSyncExternalStore } from "react";
import { onAuthStateChanged } from "firebase/auth";

import { auth } from "../backend/utils/init-firebase";
import {
  getCachedSignedExtractedImageUrl,
  getExtractedImageKeyFromStoredUrl,
  getSignedExtractedImageUrl,
} from "../backend/utils/extracted-image-urls";
import { getSignedInFirebaseUser } from "../backend/utils/anonymous-read-session";

const subscribeToFirebaseUser = (onChange: () => void) => onAuthStateChanged(auth, onChange);
const getFirebaseUser = () => getSignedInFirebaseUser();

type ResolvedImageUrl = { requestIdentity: string; displayUrl: string | undefined };

/**
 * The URL to put in an `<img src>` for a stored image URL.
 *
 * Every URL outside the private extracted-images bucket (data:, blob:, other
 * hosts and buckets) is returned unchanged. A stored extracted-image URL is
 * exchanged for a short-lived signed URL: `undefined` while it is being
 * signed, then the signed URL. If signing fails (signed out, refused, network),
 * the stored URL is returned so the image fails to load and the caller's
 * existing error handling runs. Never save the returned URL: the stored URL
 * stays the image's identity.
 */
export function useDisplayableImageUrl(storedUrl: string | null | undefined): string | undefined {
  // A session can be restored after the first render; sign again for the new account.
  const authenticatedUser = useSyncExternalStore(subscribeToFirebaseUser, getFirebaseUser, getFirebaseUser);
  const authenticatedUserId = authenticatedUser?.uid ?? null;
  const imageKey = getExtractedImageKeyFromStoredUrl(storedUrl);
  const requestIdentity = imageKey ? JSON.stringify([authenticatedUserId, imageKey]) : "not-required";
  const [resolvedImageUrl, setResolvedImageUrl] = useState<ResolvedImageUrl | null>(null);

  useEffect(() => {
    if (!imageKey) return;
    // Keep even a cached URL in state: the cache entry expires, but an image
    // already showing it must not lose its source on a later render.
    const cachedSignedUrl = getCachedSignedExtractedImageUrl(imageKey);
    if (cachedSignedUrl) {
      setResolvedImageUrl({ requestIdentity, displayUrl: cachedSignedUrl });
      return;
    }
    let isCurrentRequest = true;
    getSignedExtractedImageUrl(imageKey).then(
      (signedUrl) => {
        if (isCurrentRequest) setResolvedImageUrl({ requestIdentity, displayUrl: signedUrl });
      },
      () => {
        if (isCurrentRequest) setResolvedImageUrl({ requestIdentity, displayUrl: storedUrl ?? undefined });
      }
    );
    return () => {
      isCurrentRequest = false;
    };
  }, [imageKey, requestIdentity, storedUrl]);

  if (!imageKey) return storedUrl ?? undefined;
  if (resolvedImageUrl?.requestIdentity === requestIdentity) return resolvedImageUrl.displayUrl;
  return getCachedSignedExtractedImageUrl(imageKey) ?? undefined;
}
